import { storage } from '../src/firebaseConfig';
import { ref, uploadString, getDownloadURL, uploadBytes } from 'firebase/storage';
import { Chapter, StoryBible, PublicationSettings } from '../src/types';
import { loadChapterContentFromStorage, resolveChapterStoragePath } from './storageChapter.service';
import { Document, Paragraph, TextRun, Packer, HeadingLevel, AlignmentType, PageBreak } from 'docx';
import { resolveChapterTitle } from './chapterTitle.service';

export interface ExportResult {
  downloadUrl: string;
  storagePath: string;
  fileName: string;
}

export const generatePublication = async (
  uid: string,
  projectId: string,
  storyBible: StoryBible,
  chapters: Chapter[],
  settings: PublicationSettings
): Promise<ExportResult> => {
  // 1. Load chapters full text
  const chapterContents: { title: string; content: string }[] = [];
  const expectedTotalChapters = storyBible.targetChapterCount || chapters.length;
  const scanLimit = Math.max(chapters.length, expectedTotalChapters);
  
  const missingChapters: number[] = [];
  const finalExportChapterNumbers: number[] = [];

  for (let i = 1; i <= scanLimit; i++) {
    let text = '';
    const num = i;
    const chap = chapters.find(c => c.chapterNumber === num || c.id === num);
    
    const pathsToTry: string[] = [];

    if (chap) {
      const resolvedPath = resolveChapterStoragePath(chap);
      if (resolvedPath) {
        pathsToTry.push(resolvedPath);
      }
    }

    const canonicalPath = `users/${uid}/projects/${projectId}/chapters/chapter-${num}.json`;
    if (!pathsToTry.includes(canonicalPath)) {
      pathsToTry.push(canonicalPath);
    }

    const prevVolId = (storyBible as any).previousVolumeId || (storyBible as any).seriesId;
    if (prevVolId) {
      const prevPath = `users/${uid}/projects/${prevVolId}/chapters/chapter-${num}.json`;
      if (!pathsToTry.includes(prevPath)) {
        pathsToTry.push(prevPath);
      }
    }

    for (const path of pathsToTry) {
      try {
        const storageChap = await loadChapterContentFromStorage(path);
        if (storageChap) {
           if (storageChap.editedText) {
             text = storageChap.editedText;
             break;
           } else if (storageChap.finalText) {
             text = storageChap.finalText;
             break;
           }
        }
      } catch (e) {
        console.warn(`[Export] Failed loading from path ${path}:`, e);
      }
    }

    if (!text && chap && (chap.editedContent || chap.content)) {
      text = chap.editedContent || chap.content || '';
      console.warn(`[Export Fallback] Chapter ${num} missing Storage path/file but has complete finalText/content in memory. Falling back to cached text.`);
    }

    if (text) {
      const roadmapChapter = storyBible.chapterPlans?.find(p => p.chapterNumber === num);
      const pseudoChap = chap || { id: num, chapterNumber: num, title: `Chapter ${num}` };
      let resolved = resolveChapterTitle(pseudoChap as Chapter, roadmapChapter, num);

      // Warning check: If export resolves to generic "Chapter X" but approved roadmap has a non-empty custom title
      const roadmapTitle = roadmapChapter ? (roadmapChapter.title || roadmapChapter.chapterTitle || roadmapChapter.name || roadmapChapter.heading) : null;
      const isGeneric = /^chapter\s+\d+$/i.test(resolved.trim());
      if (isGeneric && roadmapTitle && roadmapTitle.trim()) {
        console.warn(`[Export Warning] Chapter ${num} resolved to generic title "${resolved}" while approved roadmap has non-empty title "${roadmapTitle}". Overriding to approved roadmap title.`);
        resolved = roadmapTitle.trim();
      }

      // Title audit logging
      let sourceUsed = 'generic_fallback';
      if (roadmapTitle && roadmapTitle.trim() && resolved === roadmapTitle.trim()) {
        sourceUsed = 'approved_roadmap';
      } else {
        const savedTitle = chap?.title || chap?.chapterTitle || chap?.name || chap?.heading;
        if (savedTitle && savedTitle.trim() && resolved === savedTitle.trim()) {
          sourceUsed = 'saved_chapter';
        }
      }

      console.log(`[Export Title Audit] Chapter ${num}:`, {
        chapterNumber: num,
        approvedRoadmapTitle: roadmapTitle || null,
        savedChapterTitle: chap?.title || null,
        exportResolvedTitle: resolved,
        sourceUsed
      });

      finalExportChapterNumbers.push(num);
      chapterContents.push({ title: resolved, content: text });
    } else {
      missingChapters.push(num);
    }
  }

  console.log(`[EXPORT BUILD SOURCE AUDIT] ${JSON.stringify({
    projectId,
    expectedTotalChapters,
    metadataFound: chapters.length,
    resolvedPathCount: chapters.filter(c => resolveChapterStoragePath(c)).length,
    readableJsonCount: finalExportChapterNumbers.length,
    hasFinalTextCount: finalExportChapterNumbers.length,
    finalExportChapterNumbers,
    finalExportChapterCount: finalExportChapterNumbers.length,
    missingChapterNumbers: missingChapters
  })}`);

  if (missingChapters.length > 0) {
    throw new Error(`Missing final text for Chapters: ${missingChapters.join(', ')}.`);
  }

  const timestamp = new Date().getTime();
  const safeTitle = (settings.bookTitle || storyBible.title || 'Export').replace(/[^a-z0-9_]/gi, '_').toLowerCase();
  const ext = settings.exportFormat.toLowerCase();
  const fileName = `${safeTitle}-${timestamp}.${ext}`;
  const exportPath = `users/${uid}/projects/${projectId}/exports/${fileName}`;
  const storageRef = ref(storage, exportPath);

  let downloadUrl = '';

  if (settings.exportFormat === 'DOCX') {
     const doc = buildDocx(settings, chapterContents);
     const blob = await Packer.toBlob(doc);
     downloadUrl = URL.createObjectURL(blob);
     
     // optionally upload to storage
     try {
       await uploadBytes(storageRef, blob, { contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
     } catch (e) {
       console.warn("Storage upload for DOCX failed, using local download only:", e);
     }
  } else {
     const text = buildPlainText(settings, chapterContents, settings.exportFormat === 'MD');
     const blob = new Blob([text], { type: settings.exportFormat === 'MD' ? 'text/markdown' : 'text/plain' });
     downloadUrl = URL.createObjectURL(blob);
     
     // optionally upload to storage
     try {
       await uploadString(storageRef, text, 'raw', {
         contentType: settings.exportFormat === 'MD' ? 'text/markdown' : 'text/plain'
       });
     } catch (e) {
       console.warn("Storage upload for plain text failed, using local download only:", e);
     }
  }

  
  // optionally we can also fetch getDownloadUrl if we want to share that instead of the local blob
  // const storageDownloadUrl = await getDownloadURL(storageRef);
  
  return {
    downloadUrl,
    storagePath: exportPath,
    fileName
  };
};

const normalizeText = (text: string): string[] => {
  return text.split(/\n+/).map(p => p.trim()).filter(p => p.length > 0);
};

const getSceneBreakText = (style: string, isMd: boolean) => {
  if (style === 'LINE') return isMd ? '---' : '_______________________';
  if (style === 'SPACE') return '';
  return '***';
};

const buildPlainText = (settings: PublicationSettings, chapters: { title: string; content: string }[], isMd: boolean): string => {
  let out = '';
  const pageBreak = isMd ? '\n\n---\n\n' : '\n\n\n\n\n\n';
  const sceneBreak = getSceneBreakText(settings.sceneBreakStyle, isMd);

  if (settings.includeTitlePage) {
    if (isMd) out += `# ${settings.bookTitle}\n\n### By ${settings.authorName}\n\n`;
    else out += `${settings.bookTitle.toUpperCase()}\n\nBy ${settings.authorName}\n\n`;
    out += pageBreak;
  }

  if (settings.includeCopyrightPage) {
    out += `Copyright © ${settings.copyrightYear} ${settings.authorName}\n`;
    out += `All rights reserved.\n`;
    if (settings.publisherName) out += `Published by ${settings.publisherName}\n`;
    out += pageBreak;
  }

  if (settings.includeDedication && settings.dedication) {
    out += `${settings.dedication}\n`;
    out += pageBreak;
  }

  if (settings.includeToc) {
    if (isMd) out += `## Table of Contents\n\n`;
    else out += `TABLE OF CONTENTS\n\n`;
    chapters.forEach((c) => {
      out += `- ${c.title}\n`;
    });
    out += pageBreak;
  }

  chapters.forEach((c) => {
    if (isMd) {
      if (settings.headingStyle === 'CENTERED' || settings.headingStyle === 'BOLD') {
        out += `## **${c.title}**\n\n`;
      } else {
        out += `## ${c.title}\n\n`;
      }
    } else {
      out += `${settings.headingStyle === 'CENTERED' ? '    ' : ''}${c.title.toUpperCase()}\n\n`;
    }

    const pars = normalizeText(c.content);
    pars.forEach(p => {
      // rough scene break detection
      if (p === '***' || p === '---' || p === '###') {
        out += `\n${sceneBreak}\n\n`;
      } else {
        const indent = settings.paragraphStyle === 'INDENTED' && !isMd ? '    ' : '';
        const space = settings.paragraphStyle === 'SPACED' || isMd ? '\n\n' : '\n';
        out += `${indent}${p}${space}`;
      }
    });
    out += pageBreak;
  });

  if (settings.includeAcknowledgments && settings.acknowledgments) {
    if (isMd) out += `## Acknowledgments\n\n`;
    else out += `ACKNOWLEDGMENTS\n\n`;
    out += `${settings.acknowledgments}\n`;
    out += pageBreak;
  }

  if (settings.includeAboutAuthor && settings.aboutAuthor) {
    if (isMd) out += `## About the Author\n\n`;
    else out += `ABOUT THE AUTHOR\n\n`;
    out += `${settings.aboutAuthor}\n`;
    out += pageBreak;
  }

  if (settings.includeAlsoBy && settings.alsoBy) {
    if (isMd) out += `## Also By ${settings.authorName}\n\n`;
    else out += `ALSO BY ${settings.authorName.toUpperCase()}\n\n`;
    out += `${settings.alsoBy}\n`;
    out += pageBreak;
  }

  return out.trim();
};

const buildDocx = (settings: PublicationSettings, chapters: { title: string; content: string }[]): Document => {
  const sections: any[] = [];
  let currentChildren: any[] = [];

  const addPageBreak = () => {
    currentChildren.push(new Paragraph({ children: [new PageBreak()] }));
  };

  if (settings.includeTitlePage) {
    currentChildren.push(
      new Paragraph({ text: settings.bookTitle, heading: HeadingLevel.TITLE, alignment: AlignmentType.CENTER }),
      new Paragraph({ text: `By ${settings.authorName}`, heading: HeadingLevel.HEADING_2, alignment: AlignmentType.CENTER }),
    );
    addPageBreak();
  }

  if (settings.includeCopyrightPage) {
    currentChildren.push(
      new Paragraph({ text: `Copyright © ${settings.copyrightYear} ${settings.authorName}`, alignment: AlignmentType.CENTER }),
      new Paragraph({ text: `All rights reserved.`, alignment: AlignmentType.CENTER }),
    );
    if (settings.publisherName) {
      currentChildren.push(new Paragraph({ text: `Published by ${settings.publisherName}`, alignment: AlignmentType.CENTER }));
    }
    addPageBreak();
  }

  if (settings.includeDedication && settings.dedication) {
    currentChildren.push(new Paragraph({ text: settings.dedication, alignment: AlignmentType.CENTER }));
    addPageBreak();
  }

  if (settings.includeToc) {
    currentChildren.push(new Paragraph({ text: 'Table of Contents', heading: HeadingLevel.HEADING_1 }));
    chapters.forEach(c => {
      currentChildren.push(new Paragraph({ text: c.title }));
    });
    addPageBreak();
  }

  chapters.forEach(c => {
    const headingAlignment = settings.headingStyle === 'CENTERED' ? AlignmentType.CENTER : AlignmentType.LEFT;
    currentChildren.push(new Paragraph({ text: c.title, heading: HeadingLevel.HEADING_1, alignment: headingAlignment }));

    const pars = normalizeText(c.content);
    pars.forEach(p => {
      if (p === '***' || p === '---' || p === '###') {
        const sb = getSceneBreakText(settings.sceneBreakStyle, false);
        currentChildren.push(new Paragraph({ text: sb, alignment: AlignmentType.CENTER, spacing: { before: 240, after: 240 } }));
      } else {
        currentChildren.push(new Paragraph({
          text: p,
          indent: settings.paragraphStyle === 'INDENTED' ? { firstLine: 720 } : undefined,
          spacing: settings.paragraphStyle === 'SPACED' ? { after: 240 } : undefined
        }));
      }
    });
    addPageBreak();
  });

  if (settings.includeAcknowledgments && settings.acknowledgments) {
    currentChildren.push(
      new Paragraph({ text: 'Acknowledgments', heading: HeadingLevel.HEADING_1 }),
      new Paragraph({ text: settings.acknowledgments })
    );
    addPageBreak();
  }

  if (settings.includeAboutAuthor && settings.aboutAuthor) {
    currentChildren.push(
      new Paragraph({ text: 'About the Author', heading: HeadingLevel.HEADING_1 }),
      new Paragraph({ text: settings.aboutAuthor })
    );
    addPageBreak();
  }

  if (settings.includeAlsoBy && settings.alsoBy) {
    currentChildren.push(
       new Paragraph({ text: `Also By ${settings.authorName}`, heading: HeadingLevel.HEADING_1 }),
       ...normalizeText(settings.alsoBy).map(p => new Paragraph({ text: p }))
    );
  }

  sections.push({ properties: {}, children: currentChildren });

  return new Document({ sections });
};
